<template>
  <div class="my-6">
    <AppFilterNavigation no-preserve class="mb-4">
      <template #default="prop">
        <div class="col-12 col-md-2 px-2 my-2 text-center">
          <AppSelectMultipleDates
            :date-from.sync="filters.dateFrom"
            :date-until.sync="filters.dateUntil"
            data-test="dates"
            :default-date-from="[defaultDate.dateFrom]"
            :default-date-until="[defaultDate.dateUntil]"
            :date-options="dateOptions"
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            :label="
              $t(
                'people:ShoppingBasket.OrderBalance.Date.label',
                'Date',
                'Date  label in Shopping basket balance page'
              )
            "
          />
        </div>
        <div class="col-12 col-md-10 px-2 my-2 text-center">
          <AppSearchBox
            :value.sync="filters.search"
            clearable
            :input-label="
              $t(
                'people:ShoppingBasket.OrderBalance.Search.label',
                'Search by keyword',
                'Search label in Shopping basket balance page'
              )
            "
          />
        </div>
      </template>
    </AppFilterNavigation>
    <v-data-iterator :items="events" :items-per-page="-1" hide-default-footer>
      <template #no-results>
        <h3 class="secondary--text text-center mb-8">
          {{ $t("events:noEventsFound", "No events found") }}
        </h3>
      </template>
      <template #no-data>
        <h3 class="secondary--text text-center mb-8">
          {{ $t("events:noEventsFound", "No events found") }}
        </h3>
      </template>
      <template #default="{ items }">
        <section
          v-for="(item, i) in items"
          :key="item.id"
          :data-scroll-id="item.id"
          class="mb-8"
        >
          <EventCardSmall :event="item" ignore-logo>
            <template #orderBalance>
              <div class="d-flex justify-end mt-5">
                <v-btn
                  class="ma-2"
                  color="primary"
                  dark
                  text
                  :aria-label="$t('common:expandButton.alt')"
                  @click.prevent="open(i, 'cashier', item.id)"
                >
                  <v-icon
                    class="mr-2"
                    :class="{
                      'counter-rotate': openIndex == i && openType == 'cashier'
                    }"
                  >
                    $iconCircleCaretDown
                  </v-icon>
                  <b>
                    {{
                      $t(
                        "people:ShoppingBasket.Events.PersonEventBalancesReport.button.label",
                        "Balances"
                      )
                    }}
                  </b>
                </v-btn>

                <v-btn
                  v-if="personAllowedFields.includes('eventFinancialReport')"
                  class="ma-2"
                  color="primary"
                  dark
                  text
                  :aria-label="$t('common:expandButton.alt')"
                  @click.prevent="open(i, 'statement', item.id)"
                >
                  <v-icon
                    class="mr-2"
                    :class="{
                      'counter-rotate':
                        openIndex == i && openType == 'statement'
                    }"
                  >
                    $iconCircleCaretDown
                  </v-icon>
                  <b>{{
                    $t(
                      "people:ShoppingBasket.Events.PersonEventFinancialReport.button.label",
                      "Full statement"
                    )
                  }}</b>
                </v-btn>
              </div>
            </template>
          </EventCardSmall>
          <div v-show="openIndex == i" class="py-2">
            <PersonCashierDetails
              v-if="openIndex == i && openType == 'cashier'"
              :event-id="item.id"
              :person="person"
              ignore-organiser
            />
            <PersonStatementReport
              v-else-if="
                personAllowedFields.includes('eventFinancialReport') &&
                  openIndex == i &&
                  openType == 'statement'
              "
              :event="item"
              :person="person"
            />
          </div>
        </section>
      </template>
    </v-data-iterator>
    <div class="text-right">
      <AppLoadMoreBtn
        :has-more-results="hasMoreResults"
        :loading="$apollo.queries.events.loading"
        @loadMore="loadMore"
      />
    </div>
  </div>
</template>
<script>
import gql from "fraql";

import EventCardSmall from "@/components/event/EventCardSmall.vue";
import AppSearchBox from "@elements/AppSearchBox.vue";
import AppSelectMultipleDates from "@AppSelects/AppSelectMultipleDates.vue";
import AppFilterNavigation from "@elements/AppFilterNavigation.vue";
import AppLoadMoreBtn from "@elements/AppLoadMoreBtn.vue";

import PersonCashierDetails from "@elements/cashier/PersonCashierDetails.vue";
import PersonStatementReport from "./elements/PersonStatementReport/index.vue";

const PER_PAGE = 10;

export default {
  name: "OrdersBalance",
  components: {
    AppSelectMultipleDates,
    AppSearchBox,
    AppLoadMoreBtn,
    AppFilterNavigation,
    PersonCashierDetails,
    EventCardSmall,
    PersonStatementReport
  },
  props: {
    person: {
      type: Object,
      required: true
    },
    refetchBalance: {
      type: Boolean,
      default: null
    }
  },
  data() {
    return {
      filters: {
        search: null,
        dateFrom: null,
        dateUntil: null,
        eventId: null
      },
      hasMoreResults: true,
      openIndex: null,
      openType: null,
      events: [],
      loading: false,
      personAllowedFields: []
    };
  },
  apollo: {
    events: {
      query: gql`
        query personBalances(
          $offset: Int
          $limit: Int
          $sortBy: String
          $sortDesc: Boolean
          $keyword: String
          $eventIds: [ID]
          $startDateFrom: String
          $startDateUntil: String
          $hasFinancialActivitiesWithPersonIds: [ID]
        ) {
          events(
            offset: $offset
            limit: $limit
            sortBy: $sortBy
            sortDesc: $sortDesc
            keyword: $keyword
            eventIds: $eventIds
            startDateFrom: $startDateFrom
            startDateUntil: $startDateUntil
            hasFinancialActivitiesWithPersonIds: $hasFinancialActivitiesWithPersonIds
          ) {
            id
            ${PersonStatementReport.fragments.eventFields}
            ${EventCardSmall.fragments.event}
          }
        }
      `,
      variables() {
        return {
          offset: 0,
          limit: PER_PAGE,
          keyword: this.filters.search,
          eventIds: [this.filters.eventId].filter(Boolean),
          hasFinancialActivitiesWithPersonIds: [this.person.id].filter(Boolean),
          startDateFrom: this.filters.dateFrom,
          startDateUntil: this.filters.dateUntil,
          sortBy: "startDate",
          sortDesc: true
        };
      },
      result({ loading, data }) {
        if (loading || !data) return;
        this.hasMoreResults = data.events.length >= PER_PAGE;
      },
      fetchPolicy: "cache-and-network"
    },
    personAllowedFields: {
      query: gql`
        query personAllowedFields($id: ID) {
          person(id: $id) {
            id
            allowedFields
          }
        }
      `,
      variables() {
        return {
          id: this.person.id
        };
      },
      fetchPolicy: "cache-first",
      update({ person }) {
        return person.allowedFields;
      }
    }
  },
  computed: {
    defaultDate() {
      const year = this.$dayjs().format("YYYY");
      return {
        dateFrom: `${year}-01-01`,
        dateUntil: `${year}-12-31`
      };
    },
    dateOptions() {
      const year = this.$dayjs().format("YYYY");
      return [
        {
          type: "custom",
          text: this.$dayjs()
            .subtract(1, "months")
            .format("MMMM"),
          dateFrom: this.$dayjs()
            .subtract(1, "months")
            .startOf("month")
            .format("YYYY-MM-DD"),
          dateUntil: this.$dayjs()
            .subtract(1, "months")
            .endOf("month")
            .format("YYYY-MM-DD")
        },
        { type: "wholeYear" },
        {
          type: "custom",
          text: year - 1,
          dateFrom: year - 1 + "-01-01",
          dateUntil: year - 1 + "-12-31"
        }
      ];
    }
  },
  watch: {
    refetchBalance() {
      this.$apollo.queries.events.refetch();
    }
  },
  created() {
    this.filters.dateFrom = this.defaultDate.dateFrom;
    this.filters.dateUntil = this.defaultDate.dateUntil;
  },
  methods: {
    loadMore() {
      this.$apollo.queries.events
        .fetchMore({
          variables: {
            offset: this.events.length
          }
        })
        .then(({ data: { events } }) => {
          this.hasMoreResults = events.length >= PER_PAGE;
        });
    },
    open(i, type, eventId) {
      if (this.openIndex == i && this.openType == type) {
        this.openIndex = null;
        this.openType = null;
        return;
      }
      this.openIndex = i;
      this.openType = type;
      window.requestAnimationFrame(() => {
        const event = this.$el.querySelector(`[data-scroll-id="${eventId}"]`);
        event.scrollIntoView(true);
      });
    }
  }
};
</script>
